import type { AxiosError } from 'axios'

export class AppError extends Error {
  public readonly code?: string
  public readonly status?: number
  public readonly originalError?: unknown

  constructor(
    message: string,
    code?: string,
    originalError?: unknown,
    status?: number
  ) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.status = status
    this.originalError = originalError

    // Error 클래스를 상속받을 때 필요한 설정
    Object.setPrototypeOf(this, AppError.prototype)
  }

  // 정적 팩토리 메서드들
  static fromAxiosError(error: AxiosError): AppError {
    // API 응답 데이터에서 메시지 추출 (타입 안전하게)
    let message = error.message || 'Network error occurred'
    
    if (error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as Record<string, unknown>
      if (typeof data.message === 'string') {
        message = data.message
      }
    }
    
    return new AppError(
      message,
      error.code,
      error,
      error.response?.status
    )
  }

  static fromError(error: Error): AppError {
    if (error instanceof AppError) {
      return error
    }
    return new AppError(error.message, undefined, error)
  }

  static fromUnknown(error: unknown): AppError {
    if (error instanceof AppError) {
      return error
    }
    if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
      return AppError.fromAxiosError(error as AxiosError)
    }
    if (error instanceof Error) {
      return AppError.fromError(error)
    }
    if (typeof error === 'string') {
      return new AppError(error, 'UNKNOWN_ERROR')
    }
    return new AppError('An unexpected error occurred', 'UNKNOWN_ERROR', error)
  }

  // 유틸리티 메서드들
  isNetworkError(): boolean {
    return this.status === undefined || this.status >= 500
  }

  isAuthError(): boolean {
    return this.status === 401 || this.status === 403
  }

  isValidationError(): boolean {
    return this.status === 400
  }

  isNotFoundError(): boolean {
    return this.status === 404
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      stack: this.stack
    }
  }
}

// 기존 ErrorHandler는 호환성을 위해 유지
export class ErrorHandler {
  static handle(error: unknown): AppError {
    return AppError.fromUnknown(error)
  }

  static isNetworkError(error: AppError): boolean {
    return error.isNetworkError()
  }

  static isAuthError(error: AppError): boolean {
    return error.isAuthError()
  }

  static isValidationError(error: AppError): boolean {
    return error.isValidationError()
  }
}
